<template>
  <div class="attachment-card">
    <div class="attachment-card__content">
      <div class="attachment-card__content-info">
        <v-icon
          class="attachment-card__content-image"
          size="40"
          color="blue-grey"
        >
          {{ fileIcon }}
        </v-icon>
        <div class="attachment-card__content-titles">
          <p data-private class="attachment-card__content-title">
            {{ attachment.fileName }}
          </p>

          <div v-if="isPending" class="attachment-card__content-uploading">
            <p class="caption">{{ $t('attachments.uploading') }}</p>
          </div>

          <div v-else class="d-flex justify-space-between">
            <p data-private class="attachment-card__content-subtitle">
              {{ attachment.size }}
            </p>

            <div class="ml-2 attachment-card__content-actions">
              <a
                data-private
                data-testid="revView-button-downloadAttachment"
                class="attachment-card__content-actions-download"
                @click="onDownload(attachment)"
                >{{ $t('common.button.download') }}</a
              >
              <a
                data-private
                data-testid="revView-button-deleteAttachment"
                v-if="isEditMode"
                class="attachment-card__content-actions-delete"
                @click="onDelete(attachment)"
                >{{ $t('common.button.delete') }}</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <DeleteConfirmationModal
      :show="showDeleteModal"
      :isLoading="isDeleting"
      :title="$t('attachments.delete_file')"
      :text="
        $t(
          'attachments.are_you_sure_you_want_to_remove_this_file_from_this_rev'
        )
      "
      @cancel="showDeleteModal = false"
      @delete="removeAttachment"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import MediaSDK from '@/lib/ReverMediaSDK'
import mixin from '@/mixins/mixin.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

export default {
  name: 'AttachmentCard',

  mixins: [mixin],

  components: {
    DeleteConfirmationModal,
  },

  props: {
    attachment: {
      type: Object,
      required: true,
      default() {
        return {}
      },
    },
    isEditMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPending: {
      type: Boolean,
      required: false,
      default: false,
    },
    revId: {
      type: String,
      required: false,
    },
    organizationId: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      showDeleteModal: false,
      isDeleting: false,
      idToDelete: '',
    }
  },

  computed: {
    ...mapState('attachments', {
      attachments(state) {
        return state.attachments
      },
    }),

    fileIcon() {
      const extension = this.attachment?.mediaObject?.fileExtension
      return this.getFileIconByExtension(extension)
    },
  },

  methods: {
    ...mapActions({
      setAttachments: 'attachments/set',
      deleteAttachment: 'attachments/delete',
    }),

    onDownload(attachment) {
      MediaSDK.download(attachment.mediaObject)
    },

    onDelete(attachment) {
      this.showDeleteModal = true
      this.idToDelete = attachment?._id?.toString() ?? ''
    },

    async removeAttachment() {
      try {
        this.isDeleting = true
        const fileIds = this.attachments.filter(
          file => file._id.toString() !== this.idToDelete
        )

        await this.deleteAttachment({
          organizationId: this.organizationId,
          attachmentId: this.idToDelete,
        })

        this.setAttachments(fileIds)
        this.isDeleting = true
        this.showDeleteModal = false
        this.showSuccess(this.$t('attachments.attachment_deleted_successfully'))
      } catch {
        this.showError(
          this.$t(
            'attachments.there_was_a_problem_deleting_this_attachment_try_again'
          )
        )
        this.isDeleting = true
        this.showDeleteModal = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.attachment-card {
  border-radius: 4px;
  box-shadow: 0px 1px 2px rgba(120, 135, 155, 0.3);
  margin: 12px 8px;
  padding: 16px;
  position: relative;
  word-break: break-word;
  &__content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    &-info {
      display: flex;
    }
    &-image {
      margin: 0 20px 0 0;
    }
    &-title {
      margin: 0;
      color: $color-blue-grey-darken3;
      font-size: 14px;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      max-width: 170px;
    }
    &-subtitle {
      margin: 0;
      color: $color-blue-grey-darken1;
      font-size: 12px;
    }
    &-actions {
      display: flex;
      align-items: center;

      cursor: pointer;

      &-delete,
      &-download {
        font-size: 12px;
        word-break: keep-all;
        text-decoration: underline;
      }

      &-delete {
        border-left: 1px solid rgba(22, 125, 236, 0.5);
        margin-left: 10px;
        padding-left: 10px;
      }
    }

    &-uploading {
      color: $color-blue-grey-darken1;
      opacity: 0.5;
    }
  }
}
</style>
